import { useEffect, useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Header from '../components/blog/Header';
import Footer from '../components/blog/Footer';
import { Container } from '../components/ui/Container';
import { Button } from '../components/ui/Button';
import SEO from '../components/seo/SEO';
import { verifyEmail } from '../store/actions/authActions';

const PageWrapper = styled.div`
  min-height: 100vh;
  background-color: ${({ theme }) => theme.colors.background};
`;

const Main = styled.main`
  ${Container} {
    padding-top: 2rem;
    padding-bottom: 3rem;
  }
`;

const Card = styled.div`
  max-width: 26rem;
  margin: 0 auto;
  padding: 2rem;
  border-radius: ${({ theme }) => theme.borderRadius['2xl']};
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: ${({ theme }) => theme.colors.card};
  text-align: center;
`;

const Title = styled.h1`
  margin: 0 0 0.75rem;
  font-size: 1.5rem;
  font-weight: 700;
`;

const Message = styled.p`
  margin: 0 0 1.25rem;
  color: ${({ theme }) => theme.colors.mutedForeground};
  font-size: 0.9375rem;
  line-height: 1.6;
`;

const ErrorText = styled.p`
  color: ${({ theme }) => theme.colors.destructive || '#dc2626'};
  font-size: 0.9375rem;
`;

export default function VerifyEmail() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setStatus('error');
      setError(t('auth.verifyMissingToken'));
      return;
    }

    dispatch(verifyEmail(token))
      .then(() => setStatus('success'))
      .catch((err) => {
        setStatus('error');
        setError(err?.response?.data?.message || t('auth.verifyFailed'));
      });
  }, [dispatch, searchParams, t]);

  return (
    <PageWrapper>
      <SEO title={t('auth.verifyTitle')} robots="noindex, follow" />
      <Header />
      <Main>
        <Card>
          <Title>{t('auth.verifyTitle')}</Title>
          {status === 'loading' ? <Message>{t('auth.verifyLoading')}</Message> : null}
          {status === 'success' ? (
            <>
              <Message>{t('auth.verifySuccess')}</Message>
              <Button type="button" onClick={() => navigate('/account')}>
                {t('auth.account')}
              </Button>
            </>
          ) : null}
          {status === 'error' ? (
            <>
              <ErrorText>{error}</ErrorText>
              <Message>
                <Link to="/account">{t('auth.account')}</Link>
                {' · '}
                <Link to="/login">{t('auth.signIn')}</Link>
              </Message>
            </>
          ) : null}
        </Card>
      </Main>
      <Footer />
    </PageWrapper>
  );
}
